import React, { useRef, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toPng } from 'html-to-image';
import ItemLoadingHolder from '../../components/LoadingHolders/ItemLoadingHolder';

const AIReportModal = ({ report, loading, noticeNo, onClose }) => {
  const contentRef = useRef(null);

  const safe = (fn, fallback = '-') => {
    try {
      const val = fn();
      if (val === undefined || val === null) return fallback;
      if (Array.isArray(val) && val.length === 0) return fallback;
      return val;
    } catch {
      return fallback;
    }
  };

  const rd = (txt) =>
    typeof txt === 'string'
      ? txt
          .replace(/[ăâ]/gi, (m) => m === m.toUpperCase() ? 'A' : 'a')
          .replace(/[ĂÂ]/g, 'A')
          .replace(/[șş]/gi, (m) => m === m.toUpperCase() ? 'S' : 's')
          .replace(/[ȘŞ]/g, 'S')
          .replace(/[țţ]/gi, (m) => m === m.toUpperCase() ? 'T' : 't')
          .replace(/[ȚŢ]/g, 'T')
          .replace(/[î]/gi, (m) => m === m.toUpperCase() ? 'I' : 'i')
          .replace(/[Î]/g, 'I')
          .replace(/[éèêë]/gi, 'e')
          .replace(/[ÉÈÊË]/g, 'E')
      : String(txt ?? '');

  const BoolBadge = ({ value, label }) => (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
      value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
    }`}>
      {label}: {value ? 'Da' : 'Nu'}
    </span>
  );

  const SectionTitle = ({ children, icon }) => (
    <h3 className="text-lg font-bold text-indigo-800 flex items-center gap-2 mb-3">
      <span className="text-xl">{icon}</span> {children}
    </h3>
  );

  const budgetColors = ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899','#64748b'];

  const riskColor = (level) => {
    if (!level) return 'bg-gray-100 text-gray-500';
    const l = level.toLowerCase();
    if (l === 'scazut' || l === 'scăzut' || l === 'low') return 'bg-green-100 text-green-700';
    if (l === 'mediu' || l === 'medium') return 'bg-yellow-100 text-yellow-700';
    if (l === 'ridicat' || l === 'high') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-500';
  };

  /* ───────────────── PDF Download ───────────────── */
  const downloadAsPDF = () => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = margin;

    const addPageIfNeeded = (needed = 30) => {
      if (y + needed > pageH - 20) {
        doc.addPage();
        y = margin;
      }
    };

    const addSectionHeader = (text) => {
      addPageIfNeeded(20);
      doc.setFillColor(99, 102, 241);
      doc.rect(margin, y, contentW, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(rd(text), margin + 3, y + 5.5);
      doc.setTextColor(0, 0, 0);
      y += 12;
    };

    const addRow = (label, value) => {
      addPageIfNeeded(10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(rd(label), margin, y + 4);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      const lines = doc.splitTextToSize(rd(String(value)), contentW - 50);
      doc.text(lines, margin + 50, y + 4);
      y += Math.max(7, lines.length * 5);
    };

    const addTable = (head, body) => {
      addPageIfNeeded(20);
      autoTable(doc, {
        startY: y,
        head: [head.map(rd)],
        body: body.map(row => row.map(c => rd(String(c)))),
        styles: { font: 'helvetica', fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
        bodyStyles: { textColor: 30 },
        margin: { left: margin, right: margin },
        tableWidth: contentW,
      });
      y = doc.lastAutoTable.finalY + 8;
    };

    // ── Header ──
    doc.setFillColor(245, 245, 255);
    doc.rect(0, 0, pageW, 35, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(67, 56, 202);
    doc.text('Licitatii-Constructii', pageW / 2, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(rd(`Raport AI - ${noticeNo || 'Procedura'}`), pageW / 2, 19, { align: 'center' });
    if (report.project_title) {
      doc.setFontSize(9);
      const titleLines = doc.splitTextToSize(rd(report.project_title), contentW);
      doc.text(titleLines, pageW / 2, 25, { align: 'center' });
    }
    y = 40;

    // ── Scoruri Complexitate ──
    addSectionHeader('Scoruri Complexitate');
    const dims = [
      { key: 'technical', label: 'Tehnic', rgb: [99,102,241] },
      { key: 'financial', label: 'Financiar', rgb: [139,92,246] },
      { key: 'bim_digital', label: 'BIM / Digital', rgb: [6,182,212] },
      { key: 'environmental', label: 'Mediu', rgb: [16,185,129] },
      { key: 'legal_regulatory', label: 'Legal / Reglementari', rgb: [245,158,11] },
    ];
    const barH = 5;
    const barMaxW = contentW - 70;
    dims.forEach(dim => {
      addPageIfNeeded(12);
      const score = Number(safe(() => report.complexity_scores?.[dim.key], 0)) || 0;
      const pct = Math.min(Math.max(score, 0), 10) / 10;
      // Label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text(rd(dim.label), margin, y + 4);
      // Background bar
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin + 45, y, barMaxW, barH, 1.5, 1.5, 'F');
      // Filled bar
      if (pct > 0) {
        doc.setFillColor(...dim.rgb);
        doc.roundedRect(margin + 45, y, barMaxW * pct, barH, 1.5, 1.5, 'F');
      }
      // Score text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(30, 30, 30);
      doc.text(`${score}/10`, margin + 45 + barMaxW + 3, y + 4);
      y += 9;
    });
    // Overall score
    addPageIfNeeded(15);
    const overallScore = safe(() => report.complexity_scores?.overall, '?');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(67, 56, 202);
    doc.text(String(overallScore), margin + contentW / 2, y + 8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(rd('Scor General din 10'), margin + contentW / 2, y + 14, { align: 'center' });
    y += 20;

    // ── Clasificare ──
    addSectionHeader('Clasificare Procedura');
    addRow('Tip', safe(() => report.procedure_classification?.type));
    addRow('Subcategorie', safe(() => report.procedure_classification?.subcategory));
    addRow('Justificare', safe(() => report.procedure_classification?.justification));
    addRow('CAPEX / OPEX', safe(() => report.capex_opex?.classification));
    addRow('Justificare CAPEX/OPEX', safe(() => report.capex_opex?.justification));
    addRow('Proiect investitii', safe(() => report.capex_opex?.is_project ? 'Da' : 'Nu'));
    y += 3;

    // ── BIM ──
    addSectionHeader('Analiza BIM');
    addRow('Factor evaluare', safe(() => report.bim_analysis?.has_bim_evaluation_factor ? 'Da' : 'Nu'));
    addRow('Detalii evaluare', safe(() => report.bim_analysis?.bim_evaluation_details));
    addRow('Cerinte minime', safe(() => report.bim_analysis?.has_bim_minimum_requirements ? 'Da' : 'Nu'));
    addRow('Detalii cerinte', safe(() => report.bim_analysis?.bim_requirements_details));
    if (safe(() => report.bim_analysis?.bim_uses?.length, 0) > 0)
      addRow('Utilizari BIM', report.bim_analysis.bim_uses.join(', '));
    if (safe(() => report.bim_analysis?.bim_standards?.length, 0) > 0)
      addRow('Standarde BIM', report.bim_analysis.bim_standards.join(', '));
    y += 3;

    // ── Financiar ──
    addSectionHeader('Analiza Financiara');
    addRow('Garantie participare', safe(() => report.financial_analysis?.bid_guarantee));
    addRow('Garantie buna executie (%)', safe(() => report.financial_analysis?.performance_guarantee_percent));
    addRow('Avans (%)', safe(() => report.financial_analysis?.advance_payment_percent));
    addRow('Termene plata', safe(() => report.financial_analysis?.payment_terms));
    addRow('Penalitati intarziere', safe(() => report.financial_analysis?.delay_penalties));
    addRow('Garantie (luni)', safe(() => report.financial_analysis?.warranty_months));
    addRow('Retineri (%)', safe(() => report.financial_analysis?.retention_percent));
    addRow('Ajustare pret', safe(() => report.financial_analysis?.price_adjustment_allowed ? 'Da' : 'Nu'));
    addRow('Subcontractare', safe(() => report.financial_analysis?.subcontracting_allowed ? 'Da' : 'Nu'));
    addRow('Subcontractare max (%)', safe(() => report.financial_analysis?.subcontracting_max_percent));
    y += 3;

    // ── Calitate ──
    addSectionHeader('Calitate si Certificari');
    addRow('ISO 9001', safe(() => report.quality_certifications?.iso_9001 ? 'Da' : 'Nu'));
    addRow('ISO 14001', safe(() => report.quality_certifications?.iso_14001 ? 'Da' : 'Nu'));
    addRow('ISO 45001', safe(() => report.quality_certifications?.iso_45001 ? 'Da' : 'Nu'));
    if (safe(() => report.quality_certifications?.other_certifications?.length, 0) > 0)
      addRow('Alte certificari', report.quality_certifications.other_certifications.join(', '));
    addRow('Nr. exp. similare', safe(() => report.quality_certifications?.similar_experience_count));
    addRow('Val. min. exp. similara', safe(() => report.quality_certifications?.similar_experience_min_value));
    addRow('Cifra afaceri min.', safe(() => report.quality_certifications?.min_annual_turnover));
    y += 3;

    // ── CAPEX/OPEX ──
    addSectionHeader('CAPEX / OPEX');
    addRow('Clasificare', safe(() => report.capex_opex?.classification));
    addRow('Justificare', safe(() => report.capex_opex?.justification));
    addRow('Este proiect', safe(() => report.capex_opex?.is_project ? 'Da' : 'Nu'));
    y += 3;

    // ── Context Proiect ──
    addSectionHeader('Context Proiect');
    addRow('Parte din proiect', safe(() => report.project_context?.is_part_of_project ? 'Da' : 'Nu'));
    addRow('Nume proiect', safe(() => report.project_context?.project_name));
    addRow('Dovada', safe(() => report.project_context?.evidence));
    y += 3;

    // ── WBS ──
    if (safe(() => report.wbs_structure?.length, 0) > 0) {
      addSectionHeader('Structura WBS');
      addPageIfNeeded(20);
      autoTable(doc, {
        startY: y,
        head: [['Nivel', 'Cod', 'Descriere'].map(rd)],
        body: report.wbs_structure.map(w => {
          const level = w.level || 1;
          const indent = '  '.repeat(level - 1);
          return [String(level), rd(w.code || '-'), rd(indent + (w.description || '-'))];
        }),
        styles: { font: 'helvetica', fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
        margin: { left: margin, right: margin },
        tableWidth: contentW,
        didParseCell: (data) => {
          if (data.section === 'body') {
            const level = report.wbs_structure[data.row.index]?.level || 1;
            if (level === 1) {
              data.cell.styles.fillColor = [99, 102, 241];
              data.cell.styles.textColor = [255, 255, 255];
              data.cell.styles.fontStyle = 'bold';
            } else if (level === 2) {
              data.cell.styles.fillColor = [224, 231, 255];
              data.cell.styles.textColor = [55, 48, 163];
            } else {
              data.cell.styles.fillColor = [249, 250, 251];
              data.cell.styles.textColor = [55, 65, 81];
            }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // ── Uniclass ──
    if (safe(() => report.uniclass_2015?.length, 0) > 0) {
      addSectionHeader('Uniclass 2015');
      addTable(['Cod', 'Titlu', 'Tabel'],
        report.uniclass_2015.map(u => [u.code || '-', u.title || '-', u.table || '-']));
    }

    // ── IDS / MVD ──
    addSectionHeader('IDS / MVD');
    addRow('Aplicabil', safe(() => report.ids_mvd?.applicable ? 'Da' : 'Nu'));
    addRow('Detalii IDS', safe(() => report.ids_mvd?.ids_details));
    addRow('MVD aplicabil', safe(() => report.ids_mvd?.mvd_applicable));
    y += 3;

    // ── Riscuri ──
    if (safe(() => report.risk_analysis?.risks?.length, 0) > 0) {
      addSectionHeader('Analiza Riscuri');
      addRow('Nivel risc general', safe(() => report.risk_analysis?.overall_risk_level));
      addTable(['Categorie', 'Descriere', 'Probabilitate', 'Impact'],
        report.risk_analysis.risks.map(r => [
          r.category || '-', r.description || '-', r.probability || '-', r.impact || '-'
        ]));
    }

    // ── Sustenabilitate ──
    addSectionHeader('Sustenabilitate');
    addRow('nZEB', safe(() => report.sustainability?.is_nzeb ? 'Da' : 'Nu'));
    addRow('Criterii verzi', safe(() => report.sustainability?.green_procurement_criteria ? 'Da' : 'Nu'));
    addRow('Clasa energetica', safe(() => report.sustainability?.energy_class));
    addRow('Accesibilitate', safe(() => report.sustainability?.accessibility_requirements ? 'Da' : 'Nu'));
    addRow('Autorizatii mediu', safe(() => report.sustainability?.environmental_permits_needed ? 'Da' : 'Nu'));
    addRow('Detalii', safe(() => report.sustainability?.details));
    y += 3;

    // ── Budget (visual) ──
    if (safe(() => report.budget_breakdown?.length, 0) > 0) {
      addSectionHeader('Structura Buget');
      const budgetBarH = 8;
      const budgetBarW = contentW;
      const bColors = [[99,102,241],[139,92,246],[6,182,212],[16,185,129],[245,158,11],[239,68,68],[236,72,153],[100,116,139]];
      // Stacked bar
      addPageIfNeeded(30);
      let bx = margin;
      report.budget_breakdown.forEach((b, i) => {
        const w = (budgetBarW * (b.percentage || 0)) / 100;
        if (w > 0) {
          doc.setFillColor(...bColors[i % bColors.length]);
          doc.roundedRect(bx, y, w, budgetBarH, 0, 0, 'F');
          if (w > 12) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6);
            doc.setTextColor(255, 255, 255);
            doc.text(`${b.percentage}%`, bx + w / 2, y + 5.5, { align: 'center' });
          }
          bx += w;
        }
      });
      y += budgetBarH + 4;
      // Legend
      report.budget_breakdown.forEach((b, i) => {
        addPageIfNeeded(8);
        doc.setFillColor(...bColors[i % bColors.length]);
        doc.rect(margin, y, 3, 3, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(60, 60, 60);
        doc.text(rd(`${b.category || '-'} (${b.percentage}%)`), margin + 5, y + 2.5);
        y += 5;
      });
      y += 4;
    }

    // ── Timeline (visual) ──
    if (safe(() => report.key_milestones?.length, 0) > 0) {
      addSectionHeader('Jaloane / Timeline');
      const totalDur = report.key_milestones.reduce((s, m) => s + (m.duration_months || 0), 0);
      const tlBarW = contentW - 35;
      const tlColors = [[99,102,241],[139,92,246],[6,182,212],[16,185,129],[245,158,11],[239,68,68],[236,72,153],[100,116,139]];
      report.key_milestones.forEach((m, i) => {
        addPageIfNeeded(12);
        const pct = totalDur > 0 ? (m.duration_months || 0) / totalDur : 0;
        const barW = Math.max(tlBarW * pct, 8);
        // Phase label
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        const phaseText = rd(m.phase || '-').substring(0, 20);
        doc.text(phaseText, margin, y + 4, { maxWidth: 30 });
        // Bar
        doc.setFillColor(...tlColors[i % tlColors.length]);
        doc.roundedRect(margin + 32, y, barW, 6, 1.5, 1.5, 'F');
        // Duration text inside bar
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(255, 255, 255);
        doc.text(`${m.duration_months || 0} luni`, margin + 32 + 2, y + 4.2);
        y += 10;
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(140, 140, 140);
      doc.text(rd(`Durata totala: ${totalDur} luni`), margin + contentW, y, { align: 'right' });
      y += 6;
    }

    // ── CPV ──
    if (safe(() => report.cpv_codes?.length, 0) > 0) {
      addSectionHeader('Coduri CPV');
      addTable(['Cod', 'Descriere', 'Categorie'],
        report.cpv_codes.map(c => [c.code || '-', c.description || '-', c.category || '-']));
    }

    // ── CAEN ──
    if (safe(() => report.caen_codes?.length, 0) > 0) {
      addSectionHeader('Coduri CAEN');
      addTable(['Cod', 'Descriere'],
        report.caen_codes.map(c => [c.code || '-', c.description || '-']));
    }

    // ── COR ──
    if (safe(() => report.cor_experts?.length, 0) > 0) {
      addSectionHeader('Experti COR');
      addTable(['Rol', 'Cod COR', 'Descriere'],
        report.cor_experts.map(c => [c.role || '-', c.cor_code || '-', c.description || '-']));
    }

    // ── Metadata ──
    addSectionHeader('Informatii Generale');
    addRow('Autoritate', safe(() => report.metadata?.contracting_authority));
    addRow('Beneficiar', safe(() => report.metadata?.beneficiary));
    addRow('Sursa finantare', safe(() => report.metadata?.funding_source));
    addRow('Valoare estimata', safe(() => `${report.metadata?.estimated_value?.toLocaleString('ro-RO')} ${report.metadata?.currency || 'RON'}`));
    addRow('Durata', safe(() => `${report.metadata?.estimated_duration_months} luni`));
    addRow('Tip constructie', safe(() => report.metadata?.building_type));
    addRow('Reabilitare', safe(() => report.metadata?.is_rehabilitation ? 'Da' : 'Nu'));
    addRow('Impact mediu', safe(() => report.metadata?.has_environmental_impact ? 'Da' : 'Nu'));
    if (safe(() => report.metadata?.evaluation_criteria?.length, 0) > 0)
      addRow('Criterii evaluare', report.metadata.evaluation_criteria.join(' | '));
    if (safe(() => report.metadata?.key_requirements?.length, 0) > 0)
      addRow('Cerinte cheie', report.metadata.key_requirements.join(' | '));
    if (safe(() => report.metadata?.ai_tags?.length, 0) > 0)
      addRow('AI Tags', report.metadata.ai_tags.join(', '));

    // ── Footer on all pages ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(
        `Licitatii-Constructii - Raport AI | Pagina ${i}/${totalPages}`,
        pageW / 2,
        pageH - 8,
        { align: 'center' }
      );
    }

    const fileName = `Raport_AI_${noticeNo || 'Procedura'}.pdf`;
    doc.save(fileName);
  };

  /* ───────────────── PNG Download ───────────────── */
  const downloadAsImage = useCallback(async () => {
    if (!contentRef.current) return;
    try {
      const el = contentRef.current;
      const prevMaxH = el.style.maxHeight;
      const prevOverflow = el.style.overflow;
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';

      const dataUrl = await toPng(el, {
        backgroundColor: '#eef2ff',
        pixelRatio: 2,
        cacheBust: true,
      });

      el.style.maxHeight = prevMaxH;
      el.style.overflow = prevOverflow;

      const link = document.createElement('a');
      link.download = `Raport_AI_${noticeNo || 'Procedura'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error generating image:', err);
    }
  }, [noticeNo]);

  /* ───────────────── Error state ───────────────── */
  if (!report || typeof report !== 'object') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="relative bg-white p-10 rounded-3xl shadow-2xl max-w-lg mx-auto">
          <button
            className="absolute top-4 right-4 text-red-500 bg-white rounded-full shadow p-2 hover:bg-red-50"
            onClick={onClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-xl font-bold text-red-600 text-center mb-3">Eroare la incarcarea raportului AI</h2>
          <p className="text-center text-gray-600 text-sm">Raportul nu a putut fi incarcat. Incearca din nou.</p>
        </div>
      </div>
    );
  }

  /* ───────────────── Computed values for radar ───────────────── */
  const complexityDims = [
    { key: 'technical', label: 'Tehnic', color: 'bg-indigo-500' },
    { key: 'financial', label: 'Financiar', color: 'bg-purple-500' },
    { key: 'bim_digital', label: 'BIM / Digital', color: 'bg-cyan-500' },
    { key: 'environmental', label: 'Mediu', color: 'bg-emerald-500' },
    { key: 'legal_regulatory', label: 'Legal / Reg.', color: 'bg-amber-500' },
  ];

  const totalMilestoneDuration = (report.key_milestones || []).reduce((s, m) => s + (m.duration_months || 0), 0);
  const milestoneColors = ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899','#64748b','#14b8a6','#f97316'];

  /* ───────────────── Main render ───────────────── */
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative bg-gradient-to-br from-white via-indigo-50 to-blue-50 rounded-3xl shadow-2xl w-11/12 max-w-5xl mx-auto max-h-[95vh] border border-indigo-100 flex flex-col">

        {/* ── Header bar ── */}
        <div className="flex items-center justify-between px-6 md:px-8 pt-6 md:pt-8 pb-4 border-b border-indigo-100 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-extrabold text-indigo-800 tracking-tight">Raport AI</h2>
            {noticeNo && <p className="text-sm text-indigo-400 font-mono">{noticeNo}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={downloadAsPDF}
              title="Descarca PDF"
              className="flex items-center gap-1.5 bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300 font-semibold py-2 px-3 rounded-xl shadow-sm transition text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v6m0 0l-2-2m2 2l2-2" />
              </svg>
              PDF
            </button>
            <button
              onClick={downloadAsImage}
              title="Descarca imagine PNG"
              className="flex items-center gap-1.5 bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 font-semibold py-2 px-3 rounded-xl shadow-sm transition text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              PNG
            </button>
            <button
              onClick={onClose}
              title="Inchide"
              className="bg-white border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300 p-2 rounded-xl shadow-sm transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        {loading ? (
          <div className="text-center py-12 px-6">
            <ItemLoadingHolder />
            <p className="mt-2 text-sm text-gray-500">Generare raport...</p>
          </div>
        ) : (
          <div ref={contentRef} className="space-y-6 overflow-y-auto flex-1 px-6 md:px-8 py-6">

            {/* ════════ Title & Classification ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <h3 className="text-xl font-bold text-indigo-800 mb-3">{safe(() => report.project_title)}</h3>
              <div className="flex flex-wrap gap-2">
                <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-full">
                  {safe(() => report.procedure_classification?.type)}
                </span>
                <span className="inline-block bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1.5 rounded-full">
                  {safe(() => report.procedure_classification?.subcategory)}
                </span>
                <span className={`inline-block text-xs font-bold px-3 py-1.5 rounded-full ${
                  safe(() => report.capex_opex?.classification) === 'CAPEX'
                    ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  {safe(() => report.capex_opex?.classification)}
                </span>
                {safe(() => report.metadata?.estimated_value, null) && (
                  <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    {safe(() => report.metadata.estimated_value.toLocaleString('ro-RO'))} {safe(() => report.metadata.currency)}
                  </span>
                )}
                {safe(() => report.metadata?.estimated_duration_months, null) && (
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    {safe(() => report.metadata.estimated_duration_months)} luni
                  </span>
                )}
              </div>
              {safe(() => report.procedure_classification?.justification, null) && (
                <p className="text-xs text-gray-500 mt-2">{safe(() => report.procedure_classification.justification)}</p>
              )}
            </div>

            {/* ════════ Complexity Radar ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u2606"}>Scoruri Complexitate</SectionTitle>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Horizontal bar chart */}
                <div className="flex-1 space-y-3 w-full">
                  {complexityDims.map((dim) => {
                    const score = safe(() => report.complexity_scores?.[dim.key], 0);
                    const pct = Math.min(Math.max(Number(score) || 0, 0), 10) * 10;
                    return (
                      <div key={dim.key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{dim.label}</span>
                          <span className="text-gray-500 font-mono text-xs">{score}/10</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                          <div
                            className={`${dim.color} h-full rounded-full transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Overall score */}
                <div className="flex flex-col items-center justify-center min-w-[120px]">
                  <span className="text-5xl font-extrabold text-indigo-700">
                    {safe(() => report.complexity_scores?.overall, '?')}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Scor General</span>
                  <span className="text-xs text-gray-400">din 10</span>
                </div>
              </div>
            </div>

            {/* ════════ BIM Analysis ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u25A3"}>Analiza BIM</SectionTitle>
              <div className="flex flex-wrap gap-2 mb-3">
                <BoolBadge value={safe(() => report.bim_analysis?.has_bim_evaluation_factor, false)} label="Factor de evaluare" />
                <BoolBadge value={safe(() => report.bim_analysis?.has_bim_minimum_requirements, false)} label="Cerinte minime" />
              </div>
              {safe(() => report.bim_analysis?.bim_evaluation_details, null) && (
                <div className="mb-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Detalii evaluare BIM</span>
                  <p className="text-sm text-gray-700">{safe(() => report.bim_analysis.bim_evaluation_details)}</p>
                </div>
              )}
              {safe(() => report.bim_analysis?.bim_requirements_details, null) && (
                <div className="mb-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Cerinte BIM</span>
                  <p className="text-sm text-gray-700">{safe(() => report.bim_analysis.bim_requirements_details)}</p>
                </div>
              )}
              {safe(() => report.bim_analysis?.bim_uses?.length, 0) > 0 && (
                <div className="mb-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Utilizari BIM</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {report.bim_analysis.bim_uses.map((u, i) => (
                      <span key={i} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{u}</span>
                    ))}
                  </div>
                </div>
              )}
              {safe(() => report.bim_analysis?.bim_standards?.length, 0) > 0 && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Standarde BIM</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {report.bim_analysis.bim_standards.map((s, i) => (
                      <span key={i} className="bg-yellow-50 text-yellow-700 text-xs px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {!safe(() => report.bim_analysis?.has_bim_evaluation_factor, false) &&
               !safe(() => report.bim_analysis?.has_bim_minimum_requirements, false) && (
                <p className="text-sm text-gray-400 italic">Nu au fost identificate cerinte BIM in fisa de date.</p>
              )}
            </div>

            {/* ════════ Financial Analysis ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u00A4"}>Analiza Financiara</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                {[
                  ['Garantie participare', safe(() => report.financial_analysis?.bid_guarantee)],
                  ['Garantie buna executie', safe(() => `${report.financial_analysis?.performance_guarantee_percent}%`)],
                  ['Avans', safe(() => `${report.financial_analysis?.advance_payment_percent}%`)],
                  ['Termene plata', safe(() => report.financial_analysis?.payment_terms)],
                  ['Penalitati intarziere', safe(() => report.financial_analysis?.delay_penalties)],
                  ['Garantie (luni)', safe(() => report.financial_analysis?.warranty_months)],
                  ['Retineri', safe(() => `${report.financial_analysis?.retention_percent}%`)],
                  ['Subcontractare max', safe(() => `${report.financial_analysis?.subcontracting_max_percent}%`)],
                ].map(([label, val], i) => (
                  <div key={i}>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
                    <p className="text-gray-800 font-medium">{val}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <BoolBadge value={safe(() => report.financial_analysis?.price_adjustment_allowed, false)} label="Ajustare pret" />
                <BoolBadge value={safe(() => report.financial_analysis?.subcontracting_allowed, false)} label="Subcontractare permisa" />
              </div>
            </div>

            {/* ════════ Quality & Certifications ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u2713"}>Calitate si Certificari</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <BoolBadge value={safe(() => report.quality_certifications?.iso_9001, false)} label="ISO 9001" />
                    <BoolBadge value={safe(() => report.quality_certifications?.iso_14001, false)} label="ISO 14001" />
                    <BoolBadge value={safe(() => report.quality_certifications?.iso_45001, false)} label="ISO 45001" />
                  </div>
                  {safe(() => report.quality_certifications?.other_certifications?.length, 0) > 0 && (
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Alte certificari</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {report.quality_certifications.other_certifications.map((c, i) => (
                          <span key={i} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Nr. experiente similare</span>
                    <p className="text-gray-800 font-medium">{safe(() => report.quality_certifications?.similar_experience_count)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Valoare min. experienta</span>
                    <p className="text-gray-800 font-medium">{safe(() => report.quality_certifications?.similar_experience_min_value)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Cifra de afaceri minima</span>
                    <p className="text-gray-800 font-medium">{safe(() => report.quality_certifications?.min_annual_turnover)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ════════ CAPEX/OPEX + Project Context ════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u25B2"}>CAPEX vs OPEX</SectionTitle>
                <p className="text-sm text-gray-700 mb-2">{safe(() => report.capex_opex?.justification)}</p>
                <BoolBadge value={safe(() => report.capex_opex?.is_project, false)} label="Este proiect de investitii" />
              </div>
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u2302"}>Context Proiect</SectionTitle>
                <BoolBadge value={safe(() => report.project_context?.is_part_of_project, false)} label="Parte din proiect mai mare" />
                {safe(() => report.project_context?.project_name, null) && (
                  <p className="text-sm text-gray-700 mt-2 font-semibold">{safe(() => report.project_context.project_name)}</p>
                )}
                {safe(() => report.project_context?.evidence, null) && (
                  <p className="text-xs text-gray-500 mt-1">{safe(() => report.project_context.evidence)}</p>
                )}
              </div>
            </div>

            {/* ════════ Budget Breakdown ════════ */}
            {safe(() => report.budget_breakdown?.length, 0) > 0 && (
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u0024"}>Structura Buget</SectionTitle>
                {/* Stacked horizontal bar */}
                <div className="w-full h-8 rounded-full overflow-hidden flex mb-4">
                  {report.budget_breakdown.map((b, i) => (
                    <div
                      key={i}
                      className="h-full flex items-center justify-center text-white text-xs font-bold transition-all"
                      style={{
                        width: `${b.percentage}%`,
                        backgroundColor: budgetColors[i % budgetColors.length],
                        minWidth: b.percentage > 3 ? undefined : '2px',
                      }}
                      title={`${b.category}: ${b.percentage}%`}
                    >
                      {b.percentage >= 8 ? `${b.percentage}%` : ''}
                    </div>
                  ))}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3">
                  {report.budget_breakdown.map((b, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-sm">
                      <span
                        className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: budgetColors[i % budgetColors.length] }}
                      />
                      <span className="text-gray-700">{b.category}</span>
                      <span className="text-gray-400 text-xs">({b.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ Timeline / Milestones ════════ */}
            {safe(() => report.key_milestones?.length, 0) > 0 && (
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u25BA"}>Jaloane / Timeline</SectionTitle>
                <div className="space-y-2">
                  {report.key_milestones.map((m, i) => {
                    const pct = totalMilestoneDuration > 0
                      ? ((m.duration_months || 0) / totalMilestoneDuration) * 100
                      : 0;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-24 text-right flex-shrink-0 truncate" title={m.phase}>
                          {m.phase}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden relative">
                          <div
                            className="h-full rounded-full flex items-center px-2 text-white text-xs font-semibold transition-all"
                            style={{
                              width: `${Math.max(pct, 5)}%`,
                              backgroundColor: milestoneColors[i % milestoneColors.length],
                            }}
                          >
                            {m.duration_months} luni
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 w-40 truncate flex-shrink-0" title={m.description}>
                          {m.description || ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2 text-right">
                  Durata totala: {totalMilestoneDuration} luni
                </p>
              </div>
            )}

            {/* ════════ WBS Structure ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u2261"}>Structura WBS</SectionTitle>
              {safe(() => report.wbs_structure?.length, 0) > 0 ? (
                <div className="space-y-0.5">
                  {report.wbs_structure.map((w, i) => {
                    const colors = [
                      'bg-indigo-600 text-white',
                      'bg-indigo-100 text-indigo-800 border border-indigo-200',
                      'bg-gray-50 text-gray-700 border border-gray-200',
                    ];
                    const codeColors = [
                      'bg-white/30 text-white',
                      'bg-indigo-50 text-indigo-600',
                      'bg-white text-gray-500',
                    ];
                    const level = Math.min((w.level || 1) - 1, 2);
                    const indent = level * 24;
                    return (
                      <div key={i} className="flex items-stretch" style={{ paddingLeft: `${indent}px` }}>
                        {level > 0 && (
                          <div className="flex items-center mr-1.5">
                            <div className={`w-px h-full ${level === 1 ? 'bg-indigo-300' : 'bg-gray-300'}`} />
                            <div className={`w-3 h-px ${level === 1 ? 'bg-indigo-300' : 'bg-gray-300'}`} />
                          </div>
                        )}
                        <div className={`flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${colors[level]}`}>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-mono font-bold ${codeColors[level]}`}>
                            {w.code}
                          </span>
                          <span className={level === 0 ? 'font-bold' : level === 1 ? 'font-semibold' : ''}>
                            {w.description}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Nu s-a generat WBS</p>
              )}
            </div>

            {/* ════════ Uniclass 2015 + IDS/MVD ════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u2630"}>Uniclass 2015</SectionTitle>
                {safe(() => report.uniclass_2015?.length, 0) > 0 ? (
                  <ul className="space-y-1.5">
                    {report.uniclass_2015.map((u, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-mono font-bold">{u.code}</span>
                        <span className="text-gray-700">{u.title}</span>
                        {u.table && <span className="text-gray-400 text-xs">({u.table})</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic">Neclasificat</p>
                )}
              </div>
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u25C7"}>IDS / MVD</SectionTitle>
                <BoolBadge value={safe(() => report.ids_mvd?.applicable, false)} label="IDS/MVD aplicabil" />
                {safe(() => report.ids_mvd?.ids_details, null) && (
                  <p className="text-sm text-gray-700 mt-2">{safe(() => report.ids_mvd.ids_details)}</p>
                )}
                {safe(() => report.ids_mvd?.mvd_applicable, null) && (
                  <p className="text-sm text-gray-600 mt-1">MVD: {safe(() => report.ids_mvd.mvd_applicable)}</p>
                )}
              </div>
            </div>

            {/* ════════ Risk Matrix ════════ */}
            {safe(() => report.risk_analysis?.risks?.length, 0) > 0 && (
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u26A0"}>Analiza Riscuri</SectionTitle>
                <div className="mb-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wider mr-2">Nivel risc general:</span>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${riskColor(safe(() => report.risk_analysis?.overall_risk_level, ''))}`}>
                    {safe(() => report.risk_analysis?.overall_risk_level)}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-indigo-50">
                        <th className="text-left px-3 py-2 text-indigo-700 font-semibold rounded-tl-lg">Categorie</th>
                        <th className="text-left px-3 py-2 text-indigo-700 font-semibold">Descriere</th>
                        <th className="text-center px-3 py-2 text-indigo-700 font-semibold">Probabilitate</th>
                        <th className="text-center px-3 py-2 text-indigo-700 font-semibold rounded-tr-lg">Impact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.risk_analysis.risks.map((r, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition">
                          <td className="px-3 py-2 font-medium text-gray-800">{r.category || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{r.description || '-'}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${riskColor(r.probability)}`}>
                              {r.probability || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${riskColor(r.impact)}`}>
                              {r.impact || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ════════ Sustainability ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u2618"}>Sustenabilitate</SectionTitle>
              <div className="flex flex-wrap gap-2 mb-3">
                <BoolBadge value={safe(() => report.sustainability?.is_nzeb, false)} label="nZEB" />
                <BoolBadge value={safe(() => report.sustainability?.green_procurement_criteria, false)} label="Criterii achizitii verzi" />
                <BoolBadge value={safe(() => report.sustainability?.accessibility_requirements, false)} label="Cerinte accesibilitate" />
                <BoolBadge value={safe(() => report.sustainability?.environmental_permits_needed, false)} label="Autorizatii mediu" />
              </div>
              {safe(() => report.sustainability?.energy_class, null) && (
                <div className="mb-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Clasa energetica</span>
                  <p className="text-sm text-gray-800 font-semibold">{safe(() => report.sustainability.energy_class)}</p>
                </div>
              )}
              {safe(() => report.sustainability?.details, null) && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Detalii</span>
                  <p className="text-sm text-gray-700">{safe(() => report.sustainability.details)}</p>
                </div>
              )}
            </div>

            {/* ════════ CPV, CAEN, COR ════════ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u2022"}>Coduri CPV</SectionTitle>
                {safe(() => report.cpv_codes?.length, 0) > 0 ? (
                  <ul className="space-y-1">
                    {report.cpv_codes.map((c, i) => (
                      <li key={i} className="text-sm">
                        <span className="font-mono text-indigo-600 font-bold">{c.code}</span>{' '}
                        <span className="text-gray-600">{c.description}</span>
                        {c.category && <span className="text-gray-400 text-xs ml-1">({c.category})</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic">-</p>
                )}
              </div>
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u2022"}>Coduri CAEN</SectionTitle>
                {safe(() => report.caen_codes?.length, 0) > 0 ? (
                  <ul className="space-y-1">
                    {report.caen_codes.map((c, i) => (
                      <li key={i} className="text-sm">
                        <span className="font-mono text-green-600 font-bold">{c.code}</span>{' '}
                        <span className="text-gray-600">{c.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic">-</p>
                )}
              </div>
              <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
                <SectionTitle icon={"\u2022"}>Experti COR</SectionTitle>
                {safe(() => report.cor_experts?.length, 0) > 0 ? (
                  <ul className="space-y-1">
                    {report.cor_experts.map((c, i) => (
                      <li key={i} className="text-sm">
                        <span className="font-bold text-gray-800">{c.role}</span>{' '}
                        <span className="text-gray-400 text-xs">({c.cor_code})</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic">-</p>
                )}
              </div>
            </div>

            {/* ════════ Metadata ════════ */}
            <div className="bg-white/80 rounded-2xl p-5 shadow border border-indigo-100">
              <SectionTitle icon={"\u2139"}>Metadata</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {[
                  ['Autoritate contractanta', safe(() => report.metadata?.contracting_authority)],
                  ['Beneficiar', safe(() => report.metadata?.beneficiary)],
                  ['Sursa finantare', safe(() => report.metadata?.funding_source)],
                  ['Tip constructie', safe(() => report.metadata?.building_type)],
                ].map(([label, val], i) => val !== '-' && (
                  <div key={i}>
                    <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
                    <p className="text-gray-800 font-medium">{val}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <BoolBadge value={safe(() => report.metadata?.is_rehabilitation, false)} label="Reabilitare" />
                <BoolBadge value={safe(() => report.metadata?.has_environmental_impact, false)} label="Impact mediu" />
              </div>
              {safe(() => report.metadata?.evaluation_criteria?.length, 0) > 0 && (
                <div className="mt-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Criterii de evaluare</span>
                  <ul className="mt-1 space-y-0.5">
                    {report.metadata.evaluation_criteria.map((c, i) => (
                      <li key={i} className="text-sm text-gray-700">- {c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {safe(() => report.metadata?.key_requirements?.length, 0) > 0 && (
                <div className="mt-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Cerinte cheie</span>
                  <ul className="mt-1 space-y-0.5">
                    {report.metadata.key_requirements.map((r, i) => (
                      <li key={i} className="text-sm text-gray-700">- {r}</li>
                    ))}
                  </ul>
                </div>
              )}
              {safe(() => report.metadata?.ai_tags?.length, 0) > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {report.metadata.ai_tags.map((t, i) => (
                    <span key={i} className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default AIReportModal;
